<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import IconBack from '../icons/IconBack.vue';
import IconDownload from '../icons/IconDownload.vue';
import IconFile from '../icons/IconFile.vue';
import IconMessage from '../icons/IconMessage.vue';
import IconStar from '../icons/IconStar.vue';
import XlButton from '../xl/XlButton.vue';
import PortalLibraryRow from './PortalLibraryRow.vue';
import { sendMine } from '../../api/messages';
import usePortalLibrary, { typeSize } from '../../composables/usePortalLibrary';
import useLibraryMarks from '../../composables/useLibraryMarks';
import useResourceDownload from '../../composables/useResourceDownload';
import { useBackTo } from '../../composables/useBackTo';
import { downloadErrorMessage, downloadFailureIsStale } from '../../helpers/helper';

/**
 * One file in the Library, /hub/library/:id (prototype resource(), build
 * 30 Sep 12:40; Hunter, 2026-09-30). Files have no description, so the page
 * has none; whether they get one is open with Hunter. Ask about this sends the
 * broker a message and stays here, where the prototype went to Messages.
 * Save here is the same mark as the list's star (XN-4682), and arriving on
 * the page is what opens the file: it stops being New (XN-4681).
 */

const route = useRoute();
const { filesOn, state, items, load } = usePortalLibrary();
const { open, download } = useResourceDownload();

// "Also in your library" moves between files on this same component: all of
// it follows the id, and nothing is refetched.
const id = computed(() => String(route.params.id));
// A file that failed to open as deleted or unshared since the lists loaded.
const goneId = ref(null);
const item = computed(() => (id.value === goneId.value
  ? null
  : items.value.find((r) => String(r.id) === id.value) ?? null));
const others = computed(() => items.value.filter((r) => String(r.id) !== id.value).slice(0, 3));

const marks = useLibraryMarks();
// Opening a file's page is what "opened" means: once per file, as soon as it is known to be one they can see.
// Wait for the marks: a load landing after the post would undo it, and staff (refused) post nothing.
watch([item, marks.state], ([r, s]) => { if (r && s !== 'loading') marks.markOpened(r.id); }, { immediate: true });
const savedShown = computed(() => marks.state.value !== 'refused');

// The window scrolls, and the router keeps its position between pages.
const titleEl = ref(null);
// Moving to another file removes the focused row; hand focus to the new title (not on first arrival).
watch(id, async (_, old) => {
  window.scrollTo(0, 0);
  if (old === undefined) return;
  await nextTick();
  titleEl.value?.focus();
}, { immediate: true });

// Back to wherever they came from: the list, My coverage's documents, a session, an email.
const { label: backLabel, go: goBack } = useBackTo({ to: '/hub/library', label: 'Your library', list: /^\/hub\/library(\?|$)/ });

async function run(action) {
  const r = item.value;
  try {
    await action(r);
  } catch (e) {
    window.loadNotification?.({ message: downloadErrorMessage(e), error: true });
    if (downloadFailureIsStale(e)) goneId.value = String(r.id);
  }
}

const sending = ref(false);
async function ask() {
  if (sending.value) return;
  sending.value = true;
  try {
    await sendMine(`I'd like some more information about ${item.value.name}.`);
    window.loadNotification?.({ message: 'Message sent to your broker.' });
  } catch (e) {
    window.loadNotification?.({
      message: e?.response?.data?.message || 'Your message could not be sent. Please try again.',
      error: true,
    });
  } finally {
    sending.value = false;
  }
}
</script>

<template>
  <section class="lib-item">
    <XlButton variant="ghost" size="sm" class="lib-item__back" data-testid="library-back" @click="goBack">
      <template #icon><IconBack color="currentColor" width="15" height="15" /></template>
      {{ backLabel }}
    </XlButton>

    <p v-if="filesOn === false" class="lib-item__calm">Nothing has been shared here yet.</p>

    <template v-else-if="filesOn">
      <div v-if="state === 'error'" class="lib-item__card lib-item__card--pad lib-item__error" role="alert">
        <p>This file could not be loaded.</p>
        <XlButton variant="ghost" data-testid="item-retry" @click="load">Retry</XlButton>
      </div>

      <div v-else-if="state === 'ready' && !item" class="lib-item__card lib-item__card--pad lib-item__gone" data-testid="item-gone">
        <IconFile color="currentColor" size="34" class="lib-item__gone-icon" />
        <b>This file is no longer available.</b>
        <RouterLink to="/hub/library" class="lib-item__back-link"><IconBack /> Back to your library</RouterLink>
      </div>

      <template v-else-if="state === 'ready'">
        <div class="lib-item__tags">
          <span v-if="item.mine" class="lib-item__tag lib-item__tag--mine">Only you</span>
          <span class="lib-item__tag">{{ typeSize(item) }}</span>
        </div>
        <h1 ref="titleEl" class="lib-item__title" tabindex="-1">{{ item.name }}</h1>

        <div class="lib-item__preview" aria-hidden="true">
          <div>
            <IconFile color="currentColor" size="44" />
            <div class="lib-item__preview-meta">{{ typeSize(item) }}</div>
          </div>
        </div>

        <div class="lib-item__actions">
          <XlButton data-testid="item-read" @click="run(open)">Read it</XlButton>
          <XlButton variant="ghost" data-testid="item-download" @click="run(download)">
            <template #icon><IconDownload color="currentColor" size="16" /></template>
            Download
          </XlButton>
          <XlButton
            v-if="savedShown"
            variant="ghost"
            :disabled="marks.state.value !== 'ready'"
            :aria-pressed="String(marks.isSaved(item.id))"
            :class="{ 'lib-item__save--on': marks.isSaved(item.id) }"
            data-testid="item-save"
            @click="marks.toggle(item.id)"
          >
            <template #icon><IconStar color="currentColor" size="16" /></template>
            {{ marks.isSaved(item.id) ? 'Saved' : 'Save' }}
          </XlButton>
          <XlButton variant="ghost" :loading="sending" data-testid="item-ask" @click="ask">
            <template #icon><IconMessage color="currentColor" size="16" /></template>
            Ask about this
          </XlButton>
        </div>

        <template v-if="others.length">
          <div class="lib-item__sh">
            <h2>Also in your library</h2>
            <span class="lib-item__ln" />
            <RouterLink to="/hub/library" data-testid="see-everything">See everything</RouterLink>
          </div>
          <div class="lib-item__card">
            <PortalLibraryRow
              v-for="r in others"
              :key="r.id"
              :item="r"
              :saved="savedShown ? marks.isSaved(r.id) : null"
              :star-disabled="marks.state.value !== 'ready'"
              :is-new="marks.isNew(r.id)"
              @toggle-save="marks.toggle"
            />
          </div>
        </template>
      </template>
    </template>
  </section>
</template>

<style scoped>
/* Prototype resource() and its .tag/.sh/.card rules (build 30 Sep 12:40). */
.lib-item__back { margin-bottom: 18px; }
.lib-item__back-link { display: inline-flex; align-items: center; gap: 8px; color: var(--xl-color-primary-text); text-decoration: none; }
.lib-item__back-link:visited, .lib-item__back-link:hover, .lib-item__back-link:focus { color: var(--xl-color-primary-text); }
.lib-item__back-link:focus-visible { outline: 2px solid var(--xl-color-primary); outline-offset: 2px; }
.lib-item__calm { color: var(--xl-color-on-surface-variant); margin: 0; }

.lib-item__tags { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.lib-item__tag {
  display: inline-flex; align-items: center; height: 23px; padding: 0 9px; border-radius: 7px;
  font-size: 11.5px; font-weight: 600; white-space: nowrap; line-height: 1;
  background: var(--xl-color-border-subtle); color: var(--xl-color-on-surface-variant);
}
.lib-item__tag--new { background: var(--xl-color-primary-container); color: var(--xl-color-primary); }
.lib-item__tag--mine { background: var(--xl-color-warning-container); color: var(--xl-color-warning); }

/* No description beneath, so the title carries the gap the paragraph had. */
.lib-item__title:focus { outline: none; }
.lib-item__title { font-size: 30px; font-weight: 600; letter-spacing: -0.03em; margin: 0 0 22px; overflow-wrap: anywhere; }

.lib-item__preview {
  height: 220px; margin-bottom: 20px; display: grid; place-items: center; text-align: center;
  border-radius: var(--xl-radius-lg); border: 1px solid var(--xl-color-border);
  background: linear-gradient(150deg, var(--xl-color-primary-container), var(--xl-color-primary-fixed-dim));
  color: var(--xl-color-primary);
}
.lib-item__preview-meta { font-size: 13.5px; font-weight: 500; margin-top: 10px; }

.lib-item__actions { display: flex; gap: 9px; flex-wrap: wrap; }
.lib-item__save--on :deep(path) { fill: currentColor; }

.lib-item__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.lib-item__sh h2 {
  font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--xl-color-outline-variant); margin: 0;
}
.lib-item__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.lib-item__sh a { font-size: 13.5px; font-weight: 600; color: var(--xl-color-primary); text-decoration: none; }

.lib-item__card {
  background: #fff; border: 1px solid var(--xl-color-border); border-radius: var(--xl-radius-lg);
  padding: 8px 22px; box-shadow: var(--xl-shadow-1);
}
.lib-item__card--pad { padding: 22px 24px; }
.lib-item__error { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.lib-item__error p { margin: 0; }
.lib-item__gone { text-align: center; padding: 44px; display: flex; flex-direction: column; align-items: center; gap: 6px; }
.lib-item__gone-icon { color: var(--xl-color-outline-variant); margin-bottom: 4px; }
.lib-item__gone b { font-size: 16px; }
.lib-item__gone a { font-size: 14px; font-weight: 600; }

@media (max-width: 480px) {
  .lib-item__title { font-size: 26px; }
  .lib-item__card { padding: 4px 14px; }
  .lib-item__card--pad { padding: 18px; }
}
</style>
